// /app/frontend/src/pages/risk/dpia/steps/ApprovalsStep.jsx
import React from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Card, CardContent } from '../../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Separator } from '../../../../components/ui/separator';
import { Plus, Trash2, UserCheck } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { REVIEW_FREQUENCIES } from '../constants';

export default function ApprovalsStep({ formData, updateField }) {
  const addApprover = () => {
    const newApprover = {
      id: uuidv4(),
      name: '',
      position: '',
      approval_date: '',
      comments: '',
      approved: false,
    };
    updateField('approvers', [...formData.approvers, newApprover]);
  };

  const updateApprover = (approverId, field, value) => {
    const updated = formData.approvers.map(approver => 
      approver.id === approverId ? { ...approver, [field]: value } : approver
    );
    updateField('approvers', updated);
  };

  const removeApprover = (approverId) => {
    updateField('approvers', formData.approvers.filter(a => a.id !== approverId));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">DPIA Approvers</h4>
          <p className="text-sm text-muted-foreground">{formData.approvers.length} approver(s)</p>
        </div>
        <Button variant="outline" size="sm" onClick={addApprover}>
          <Plus className="h-4 w-4 mr-2" /> Add Another Approver
        </Button>
      </div>

      {formData.approvers.map((approver, idx) => (
        <Card key={approver.id}>
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input 
                    value={approver.name}
                    onChange={(e) => updateApprover(approver.id, 'name', e.target.value)}
                    placeholder="Approver name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Position *</Label>
                  <Input 
                    value={approver.position}
                    onChange={(e) => updateApprover(approver.id, 'position', e.target.value)}
                    placeholder="Approver position"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Approval Date</Label>
                  <Input 
                    type="date"
                    value={approver.approval_date}
                    onChange={(e) => updateApprover(approver.id, 'approval_date', e.target.value)}
                  />
                </div>
              </div>
              <Button variant="ghost" size="sm" className="ml-2" onClick={() => removeApprover(approver.id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {formData.approvers.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-center text-muted-foreground">
            <UserCheck className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>No approvers added. Click "Add Another Approver" to add sign-off requirements.</p>
          </CardContent>
        </Card>
      )}

      <Separator />

      <div>
        <h4 className="font-medium mb-4">Review Schedule</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Review Frequency</Label>
            <Select 
              value={formData.review_frequency}
              onValueChange={(v) => updateField('review_frequency', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REVIEW_FREQUENCIES.map(freq => (
                  <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Review Owner</Label>
            <Input 
              value={formData.review_owner}
              onChange={(e) => updateField('review_owner', e.target.value)}
              placeholder="Person responsible for review"
            />
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select 
              value={formData.review_status}
              onValueChange={(v) => updateField('review_status', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}
